import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';
import NigerianPhoneInput, { formatPhoneForApi } from '../shared/NigerianPhoneInput';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.submitContact({
        ...formData,
        phone: formData.phone ? formatPhoneForApi(formData.phone) : ''
      });
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="Contact"
        description="Get in touch with Crowns by Preshy. Reach out via WhatsApp, email, or our contact form for bookings, inquiries, and custom styling requests."
        path="/contact"
      />
      <Header />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h1 className="font-serif text-4xl lg:text-5xl text-[#3E2723] mb-4 tracking-tight">Get in Touch</h1>
            <p className="text-[#786E66] text-lg">We'd love to hear from you</p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Contact Information</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-[#3E2723] flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Email</p>
                    <p className="text-[#786E66]">crownbypreshy@gmail.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F3EBE1] rounded-2xl p-8">
                <h3 className="font-serif text-xl text-[#3E2723] mb-4">Business Hours</h3>
                <div className="space-y-2 text-[#786E66]">
                  <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                  <p>Saturday: 10:00 AM - 6:00 PM</p>
                  <p>Sunday: By Appointment Only</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8" data-testid="contact-form">
                <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                      placeholder="your.email@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <NigerianPhoneInput
                    value={formData.phone}
                    onChange={(digits) => setFormData({ ...formData, phone: digits })}
                    required={false}
                    testId="contact-phone-input"
                    label="Phone (Optional)"
                  />

                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none resize-none"
                      placeholder="How can we help you?"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    data-testid="contact-submit-button"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
